//-- Copyright (c) 2021 TMT Insights Group, LLC
import React, { memo, useEffect, useRef } from 'react';
import { isEqual } from 'lodash';
import { RJSFSchema } from '@rjsf/utils';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import useRegisterOrderFormRef from '@/components/_hooks/useRegisterOrderFormRef';
import { SchemaFormRefSource } from '@/redux/slices/slice_types';

interface Props {
  orderSpecSchema?: RJSFSchema;
  orderSpecInputs?: Record<string, any>;
  orderSpecSchemaFormBeforeMessage?: React.ReactNode;
}

const OPOrderSpecSchemaFormReadOnly = ({ orderSpecSchema, orderSpecInputs, orderSpecSchemaFormBeforeMessage }: Props) => {
  const previousSchemaRef = useRef<RJSFSchema | undefined>(undefined);
  const isSchemaChangingRef = useRef(false);

  const formRef = useRef<any>(null);
  useRegisterOrderFormRef({
    disabled: false,
    formRef,
    key: SchemaFormRefSource.PROFILE_SPEC_INFO
  });

  // Basically all of the below unnecesarily complex logic is to ensure that the readonly form re-validates on teh save and continue button press
  // and properly clears errors when updating values or switching between profiles
  useEffect(() => {
    if (orderSpecSchema && !isEqual(orderSpecSchema, previousSchemaRef.current)) {
      previousSchemaRef.current = orderSpecSchema;
      isSchemaChangingRef.current = true;

      if (formRef?.current?.setState) {
        formRef.current.setState({ errorSchema: {} });
      }

      setTimeout(() => {
        isSchemaChangingRef.current = false;
      }, 10);
    }
  }, [orderSpecSchema]);

  useEffect(() => {
    if (!orderSpecSchema?.properties || !Object.keys(orderSpecSchema.properties).length || !formRef?.current) return;

    setTimeout(() => {
      if (formRef?.current?.state?.errorSchema && Object.keys(formRef.current.state.errorSchema).length > 0) {
        formRef.current.setState({ errorSchema: {} });
      }
    }, 0);
  }, [orderSpecInputs]);

  useEffect(() => {
    if (!orderSpecSchema?.properties || !Object.keys(orderSpecSchema.properties).length || !formRef?.current || isSchemaChangingRef.current) {
      return;
    }

    if (!formRef.current.state?.errorSchema || Object.keys(formRef.current.state.errorSchema).length === 0) {
      if (formRef.current.validateForm) {
        formRef.current.validateForm();
      }
    }
  }, [orderSpecSchema]);

  if (!orderSpecSchema?.properties || !Object.keys(orderSpecSchema.properties).length) {
    return null;
  }

  return (
    <>
      {orderSpecSchemaFormBeforeMessage}
      <SchemaForm tagName="div" schema={orderSpecSchema} formRef={formRef} formData={orderSpecInputs} readonly />
    </>
  );
};

export default memo(OPOrderSpecSchemaFormReadOnly);
